// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE PAGES A NON-BORROWER OPENS FROM A LINK (Jesse, 2026-10-29): the private lender's VOM, the landlord's VOR, the
// rent-free owner's certification, the gift donor's letter, the listing agent's disclosure upload and offer review,
// the seller's (and the counter-signer's) PSA signing page, and the broker's client's authorization. Each is a public
// route in App.js (no RequireAuth) opened with a token from an email.
//
//   · the Feedback slider is never shown on them (feedbackTabAllowed below — the rule PassiveFeedbackTab follows);
//   · their header is the real logo (components/ThirdPartyLogo), never a text wordmark;
//   · their type is the brand face (BRAND_FONT_STACK — tailwind.config.js fontFamily.sans / index.css body).
//
// ⚠️ NO IMPORTS IN THIS FILE. backend/test/thirdPartyPagesBrand.test.js loads it as plain JS to test the rule itself,
// and checks this list against App.js — a new public token route that is not listed here fails that test.
// ─────────────────────────────────────────────────────────────────────────────

/** Route paths as App.js declares them (a `:token` segment is matched as any one segment). */
export const THIRD_PARTY_ROUTES = Object.freeze([
  '/vom',                 // VOMForm — the private lender
  '/vor',                 // VORForm — the landlord
  '/rent-free',           // RentFreeForm — the owner the borrower lives with
  '/gift-letter',         // GiftLetter — the gift donor
  '/disclosure/:token',   // DisclosurePortal — the listing agent
  '/offer-review/:token', // OfferReview — the listing agent
  '/sign-ab-psa',         // ABPsaSigningPage — the sellers (and the counter-signer)
  '/authorize',           // BorrowerAuthorizationSign — the broker's client, before any account
]);

/** True when `pathname` is one of the third-party pages. */
export function isThirdPartyPath(pathname) {
  const p = String(pathname || '/').replace(/\/+$/, '') || '/';
  return THIRD_PARTY_ROUTES.some((r) => {
    if (!r.includes(':')) return p === r;
    const base = r.slice(0, r.indexOf('/:'));
    return p.startsWith(`${base}/`) && p.length > base.length + 1 && !p.slice(base.length + 1).includes('/');
  });
}

/**
 * 🔑 THE FEEDBACK SLIDER IS FOR A SIGNED-IN BORROWER OR PARTNER ONLY. Never on a third-party page (whoever is signed in
 * on that device), never in the staff area or the guided shell (both as before), in the partner portal only with a
 * partner session, and elsewhere only with a borrower session — a signed-out visitor's tab was inert anyway
 * (SurveyContext.triggerSurvey returns without a token).
 */
export function feedbackTabAllowed({ pathname, borrowerSignedIn = false, partnerSignedIn = false } = {}) {
  const p = String(pathname || '/');
  if (isThirdPartyPath(p)) return false;
  if (p.startsWith('/admin')) return false;
  if (p.startsWith('/apply/guided')) return false;
  if (p.startsWith('/doorlypro')) return !!partnerSignedIn;
  return !!borrowerSignedIn;
}

/** The brand face, as tailwind.config.js fontFamily.sans and index.css body declare it. */
export const BRAND_FONT_STACK = "'Inter', ui-sans-serif, system-ui, sans-serif";

/** The logo the app header uses (components/Navbar, AppShell) — 1200 × 333. */
export const BRAND_LOGO_PATH = '/doorly_logo_black.png';
